import { useEffect, useState } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import { useMutation } from '@apollo/client'
import { CHARTER_BOAT_BOOKING_STATUS } from 'graphql/charterBoats/mutations'

export function UpdateBoatStatusDialog({ status, title, onClose, onSuccess, booking }) {
    const { t } = useTranslation()
    const open = status

    const [bookingStatus, setBookingStatus] = useState('')
    const [showUpdateConfirm, setShowUpdateConfirm] = useState(false)
    const [submitLoading, setSubmitLoading] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [addError, setAddError] = useState(t('Problem Updating Booking'))

    const statusMap = {
        APR: 'Approved',
        REJ: 'Rejected',
    }

    // Initialize status from booking
    useEffect(() => {
        if (booking?.status?.code) {
            setBookingStatus(booking.status?.code)
        }
    }, [booking])

    // Reset dialog state when opened/closed
    useEffect(() => {
        if (status) {
            setShowUpdateConfirm(false)
        } else {
            setBookingStatus('')
            setShowUpdateConfirm(false)
        }
    }, [status])

    const isValid = bookingStatus !== ''

    const [updateCharterBoatBooking] = useMutation(CHARTER_BOAT_BOOKING_STATUS, {
        onCompleted: data => {
            if (data.updateCharterBoatBookingStatus.code === '200' && data.updateCharterBoatBookingStatus.success) {
                setSubmitLoading(false)
                onSuccess()
                onClose()
            } else {
                setSubmitLoading(false)
                setAddError(data.updateCharterBoatBookingStatus.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setSubmitLoading(false)
            setAddError(error.message)
            setShowErrDialog(true)
        },
    })

    const handleUpdateConfirm = () => {
        setSubmitLoading(true)

        updateCharterBoatBooking({
            variables: {
                bookingId: booking.booking_id,
                statusCode: bookingStatus,
            },
        })
    }

    return (
        <Dialog open={open} onClose={onClose} className="relative z-10">
            <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75" />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            {!showUpdateConfirm && (
                                <>
                                    <h3 className="text-lg font-bold text-primary ps-2">{title}</h3>
                                    <div className="mt-6 space-y-4">
                                        <div>
                                            <label className="text-sm font-medium">{t('Booking Status')} *</label>
                                            <select
                                                value={bookingStatus}
                                                onChange={e => setBookingStatus(e.target.value)}
                                                className="block w-full rounded-md border py-2 px-3 mt-2"
                                            >
                                                <option value="">{t('Select Status')}</option>
                                                {Object.entries(statusMap).map(([code, label]) => (
                                                    <option key={code} value={code}>
                                                        {label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {!showUpdateConfirm && (
                            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                <div className="flex justify-end space-x-2">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                    >
                                        {t('Cancel')}
                                    </button>

                                    <button
                                        disabled={!isValid}
                                        type="button"
                                        onClick={() => setShowUpdateConfirm(true)}
                                        className={`inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm ${
                                            isValid
                                                ? 'bg-primary hover:bg-primary/[0.9]'
                                                : 'bg-gray-400 cursor-not-allowed'
                                        }`}
                                    >
                                        {t('Save')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {showUpdateConfirm && (
                            <>
                                <div className="bg-white px-4 pb-4 pt-0 sm:pl-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                                        </div>
                                        <div className="mt-3 sm:ml-4 sm:mt-0 sm:text-left pb-3">
                                            <DialogTitle className="text-md font-bold">
                                                {t('Are you sure?')}
                                            </DialogTitle>

                                            <div className="mt-2 space-y-1">
                                                <p className="text-sm text-gray-700">
                                                    {t('Status')}: <span className="font-semibold">{statusMap[bookingStatus] || bookingStatus}</span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            onClick={() => setShowUpdateConfirm(false)}
                                            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                        >
                                            {t('No')}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={handleUpdateConfirm}
                                            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                                        >
                                            {t('Yes')}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {showErrDialog && (
                            <ErrorDialog
                                status={true}
                                title="Error"
                                message={addError}
                                onClose={() => setShowErrDialog(false)}
                            />
                        )}

                        {submitLoading && (
                            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                            </div>
                        )}
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
}
